import { registerNewUser } from "@/api/auth";
import GoogleSignIn from "@/components/google-sign-in";
import HotpleLogo from "@/components/hotple-logo";
import CustomTextInput from "@/components/upload-comps/custom-textfield";
import CustomWarningPasswordInput from "@/components/upload-comps/custom-warning-password-field";
import CustomWarningTextInput from "@/components/upload-comps/custom-warning-textfield";
import SolidButton from "@/components/ui/Buttons/SolidButton";
import Modal from "@/components/ui/modal";
import Span from "@/components/ui/Span";
import { isReservedProfileIdentifier } from "@/constants/routes";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";

const USERNAME_UPPERCASE_REGEX = /[A-Z]/;

const UserRegisterModal = ({
  isRegisterModelOpen,
  setIsRegisterModelOpen,
  setIsLoginModalOpen,
}) => {
  const loginModalT = useTranslations("login_modal");

  const [formLoader, setFormLoader] = useState(false);
  const [username, setUsername] = useState("");
  const [fullName, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmptyError, setShowEmptyError] = useState(false);
  const [userNameError, setUserNameError] = useState(false);
  const [fullNameError, setFullNameError] = useState(false);
  const hasShownUppercaseUsernameAlertRef = useRef(false);

  const syncViewportAfterClose = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === "function") {
      activeElement.blur();
    }

    const dispatchResize = () => {
      window.dispatchEvent(new Event("resize"));
    };

    window.requestAnimationFrame(() => {
      dispatchResize();
      window.setTimeout(dispatchResize, 250);
    });
  };

  const closeModal = () => {
    syncViewportAfterClose();
    setIsRegisterModelOpen(false);
  };

  const onSubmit = async () => {
    if (
      email.length === 0 ||
      password.length === 0 ||
      username.length === 0 ||
      fullName.length === 0
    ) {
      setShowEmptyError(true);
      return;
    }

    const regex = /^(?!.*[_.]{2})[a-z0-9._]+$/;

    if (USERNAME_UPPERCASE_REGEX.test(username)) {
      enqueueSnackbar(loginModalT("username_uppercase_not_allowed"), {
        variant: "error",
      });
      return;
    }

    if (!regex.test(username)) {
      enqueueSnackbar(
        loginModalT("username_invalid_characters"),
        { variant: "error" }
      );
      return;
    }

    if (isReservedProfileIdentifier(username)) {
      enqueueSnackbar(loginModalT("username_reserved"), {
        variant: "error",
      });
      return;
    }

    try {
      setFormLoader(true);
      setShowEmptyError(false);
      const responseFromRegisterAPI = await registerNewUser({
        username,
        email,
        password,
        name: fullName,
      });

      if (responseFromRegisterAPI?.data?.error) {
        enqueueSnackbar(responseFromRegisterAPI?.data?.error || "Error", {
          variant: "error",
        });
      }
      if (responseFromRegisterAPI?.status === 201) {
        closeModal();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        { variant: "error" }
      );
    } finally {
      setFormLoader(false);
    }
  };

  return (
    <Modal
      isOpen={isRegisterModelOpen}
      onClose={() => closeModal()}
      // title="가입"
      // title={loginModalT("sign_up")}
      className={
        "md:rounded-2xl !h-full md:!h-auto md:!max-w-[400px] !w-full overflow-y-auto hide-scrollbar dark:!bg-[#0A0A0A]"
      }
      contentClass={"!p-0 h-full"}
      titleClass="!p-0 border-none "
      IconclassName="absolute top-2 right-2"
    >
      <div className="flex justify-center items-center w-full flex-col p-6">
        <HotpleLogo className={"h-10 w-auto object-contain mt-4"} />
        <p className="text-center text-base sm:text-lg font-semibold w-4/5 py-3 sm:py-4 mx-auto text-light_secondary_label dark:!text-dark_tertiary_label">
          {loginModalT("sign_up_message")}
        </p>
        <GoogleSignIn
          onClose={closeModal}
          btnClass={"py-3"}
          btnText={loginModalT("sign_in_with_google")}
        />
        <div className="w-full py-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="w-full h-[1px] dark:bg-white/15"></div>
          <p className="dark:text-white/30 text-sm">{loginModalT("or")}</p>
          <div className="w-full h-[1px] dark:bg-white/15"></div>
        </div>
        <div className="flex flex-col gap-2 w-full mb-4 sm:mb-10">
          <div>
            <CustomWarningTextInput
              name="username"
              placeholder={loginModalT("enter_username")}
              className={"!py-0 sm:!py-1.5 md:!py-1 !w-full !border-none !px-0 !text-sm sm:text-base"}
              value={username}
              inputMainClass={
                "!rounded-md !px-3 !gap-1 justify-between dark:!border-neutral-800 !border-neutral-200"
              }
              onChange={(e) => {
                const nextUsername = e.target.value;

                if (userNameError) {
                  setUserNameError(false);
                }

                if (USERNAME_UPPERCASE_REGEX.test(nextUsername)) {
                  if (!hasShownUppercaseUsernameAlertRef.current) {
                    enqueueSnackbar(
                      loginModalT("username_uppercase_not_allowed"),
                      { variant: "error" }
                    );
                    hasShownUppercaseUsernameAlertRef.current = true;
                  }
                } else {
                  hasShownUppercaseUsernameAlertRef.current = false;
                }

                setUsername(nextUsername);
              }}
              showEmptyError={showEmptyError}
              emptyErrorMsg={loginModalT("username_is_required")}
              // icon="ph:user-circle-light"
              warningIcon="ph:warning-octagon-fill"
            />
            <Span className="text-xs pt-0.5">
              www.hotple.com/{username}
            </Span>
            <Span className="block">{loginModalT("username_instructions")}</Span>
            {userNameError && (
              <p className="-ml-[240px] -mt-[30px] !text-red">
                *{loginModalT("username_is_required")}
              </p>
            )}
          </div>
          <div>
            <CustomWarningTextInput
              name="fullname"
              placeholder={loginModalT("enter_full_name")}
              className={"!py-0 sm:!py-1.5 md:!py-1 !w-full !border-none !px-0 !text-sm sm:text-base"}
              value={fullName}
              inputMainClass={
                "!rounded-md !px-3 !gap-1 justify-between dark:!border-neutral-800 !border-neutral-200"
              }
              onChange={(e) => {
                if (fullNameError) {
                  setFullNameError(false);
                }
                setFullname(e.target.value);
              }}
              showEmptyError={showEmptyError}
              emptyErrorMsg={loginModalT("nickname_is_required")}
              // icon="ph:user-circle-light"
              warningIcon="ph:warning-octagon-fill"
            />
            {/* <p className="text-xs dark:text-white/30 pt-0.5">Your nickname can only be changed once every 7 days.</p> */}
            {fullNameError && (
              <p className="-ml-[240px] -mt-[30px] !text-red">
                *{loginModalT("nickname_is_required")}
              </p>
            )}
            <Span className={"pl-1"}>{loginModalT("name_change_warning")}</Span>
          </div>
          <CustomWarningTextInput
            name="email"
            placeholder={loginModalT("enter_email_here")}
            className={"!py-0 sm:!py-1.5 md:!py-1 !w-full !border-none !px-0 !text-sm sm:text-base"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMainClass={
              "!rounded-md !px-3 !gap-1 justify-between dark:!border-neutral-800 !border-neutral-200"
            }
            showEmptyError={showEmptyError}
            emptyErrorMsg={loginModalT("email_is_required")}
            // icon="material-symbols-light:mail-outline-rounded"
            warningIcon="ph:warning-octagon-fill"
          />
          <CustomWarningPasswordInput
            name="password"
            placeholder={loginModalT("enter_password_here")}
            className={"!py-0 sm:!py-1.5 md:!py-1 !w-full !border-none !px-0 !text-sm sm:text-base"}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            passMainClass={
              "!rounded-md !px-3 !gap-1 justify-between dark:!border-neutral-800 relative !border-neutral-200 !text-sm sm:text-base"
            }
            showEmptyError={showEmptyError}
            emptyErrorMsg={loginModalT("password_is_required")}
            // icon="uit:key-skeleton-alt"
            warningIcon="ph:warning-octagon-fill"
          />
        </div>
        {/* <div className="py-5">
          <p className="w-full m-auto text-center text-xs dark:text-white/50 leading-4 font-light">
            {loginModalT("by_signing_up_you_agree_to_our")}{" "}
            <span className="dark:text-white cursor-pointer text-red">
              {loginModalT("terms")}
            </span>{" "}
            ,
            <span className="dark:text-white cursor-pointer text-red">
              {" "}
              {loginModalT("privacy_policy")}
            </span>{" "}
            {loginModalT("and")}
            <span className="dark:text-white cursor-pointer text-red">
              {" "}
              {loginModalT("cookies_policy")}
            </span>{" "}
            .
          </p>
        </div> */}
        <SolidButton
          type="button"
          onClick={onSubmit}
          loading={formLoader}
          className="w-full bg-red font-bold my-2"
        >
          {loginModalT("sign_up")}
        </SolidButton>
        <p className="w-full pt-2 sm:pt-5 text-sm font-normal dark:text-white flex justify-center items-center gap-2">
          {loginModalT("have_an_account")}
          <span
            className="text-red cursor-pointer hover:underline"
            onClick={() => {
              syncViewportAfterClose();
              setIsLoginModalOpen(true);
              setIsRegisterModelOpen(false);
            }}
          >
            {loginModalT("login")}
          </span>
        </p>
      </div>
    </Modal>
  );
};

export default UserRegisterModal;
